import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Table, Dropdown, Form, Button, Tag, message } from 'antd'
import {
    EditOutlined,
} from '@ant-design/icons';

// Constants
import { TEMPLATE } from '../../../../constants'

// Components
import { EmptyIndicator } from '../../../../components/Indicator'
import { PrimaryButton } from '../../../../components/Decoration/Button';
import { Loading, ModalImage, ButtonLink, ModalStandard } from '../../../../components'

// Services
import { TIME, REDUX } from '../../../../services/util'

// Redux Actions
import * as REDUX_ACTIONS from '../../../../services/redux/actions'

// Handler
import {
    table_handler,
    xlsx_handler,
    uploadHDLR,
    templateHDLR
} from '../../../../handlers'
import { useNavigate } from 'react-router';

const { getColumnSearchProps, EditableCell } = table_handler
const { parseDate, getCurTime } = TIME

const {
    STATUS_LABEL,
    DRIVER_LICENSE_STATUS_BY_CODE,
    DRIVER_LICENSE_STATUS_COLOR_BY_CODE
} = TEMPLATE.DRIVER

const DriverPanel = () => {

    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [ form ] = Form.useForm()

    const { uid, name: uname } = useSelector(REDUX.reduxStoreWrapper('user'))

    const drivers = useSelector(REDUX.reduxStoreWrapper('drivers.byID'))
    const users = useSelector(REDUX.reduxStoreWrapper('userHierarchy.byID'))
    const style = useSelector(REDUX.reduxStoreWrapper('style'))

    const [ dataSource, setDataSource ] = useState([])
    const [ isLoaded, setIsLoaded ] = useState(false);
    const [ rec, setRec ] = useState({})
    const [ isOpen, setIsOpen] = useState(false);
    
    const searchTextState = useState('')
    const searchedColumnState = useState('')

    const usePrevious = (value) => {
        const ref = useRef();
        useEffect(() => {
          ref.current = value;
        });
        return ref.current;
    }

    const prevVehicles = usePrevious(drivers);

    const refreshDataSrc = () => {
        const newDataSource = Object.values(drivers)
            .map(d => {
                const company = users[d?.company]?.name || '-'

                const licenseStatusHDLR = (expiryDate) => {
                    const curTime = getCurTime()
                    const diff = expiryDate - curTime

                    const daysLeft = Math.floor(diff / (1000 * 60 * 60 * 24));
                    
                    let licenseStatus

                    if (daysLeft <= 30 && daysLeft >= 0) {
                        licenseStatus = '001'
                    } else if (daysLeft < 0) {
                        licenseStatus = '002'
                    } else {
                        licenseStatus = '000'
                    }
                    return licenseStatus
                }
                
                return {
                    ...d,
                    key: d.drvid,
                    company,
                    score: (typeof d.score === 'number' && !isNaN(d.score)) && d.score?.toFixed(2) || '-',
                    licenseStatus: licenseStatusHDLR(d.licenseExpiredAt)
            }})

        setDataSource(newDataSource)
    }

    const handleImg = (data) => {
        setIsOpen(true);
        setRec(data);
    }

    const columns = [
        {
          title: "Driver Name",
          dataIndex: "name",
          fixed: 'left',
          width: 250,
          ...getColumnSearchProps("Driver Name", 'name', searchTextState, searchedColumnState),
        },
        {
          title: "Status",
          dataIndex: "status",
          filters: Object.values(STATUS_LABEL).map(sl => ({
            text: <Tag color={STATUS_LABEL[sl.status]?.color}>{STATUS_LABEL[sl.status]?.label}</Tag>,
            value: sl.status
        })),
        onFilter: (value, record) => record.status === Number(value),
        render: (data) => <Tag color={STATUS_LABEL[data]?.color}>{STATUS_LABEL[data]?.label}</Tag>,
        },
        {
          title: "Driver Score",
          dataIndex: "score",
          ...getColumnSearchProps("Driver Score", 'score', searchTextState, searchedColumnState),
        },
        {
          title: "Reference ID",
          dataIndex: "refid",
          ...getColumnSearchProps("Reference ID", 'refid', searchTextState, searchedColumnState),
          render: (data) => data || "-",
        },
        {
          title: "Contact",
          dataIndex: "contact",
          ...getColumnSearchProps("Contact", 'contact', searchTextState, searchedColumnState),
        },
        {
          title: "IC Number",
          dataIndex: "icNumber",
          ...getColumnSearchProps("IC Number", 'ic', searchTextState, searchedColumnState),
        },
        {
          title: "IC Serial Number",
          dataIndex: "icSerialNumber",
          ...getColumnSearchProps("IC Serial Number", "icSerialNumber", searchTextState, searchedColumnState),
          render: (data) => data || "-",
        },
        {
          title: "License Expiry",
          dataIndex: "licenseExpiredAt",
          sorter: (a, b) => a.licenseExpiredAt - b.licenseExpiredAt,
          render: data => parseDate(Number(data)),
        }, 
        {
            title: "Attachment",
            render: (record) => {
            const { ic } = record;

             return ic?.length > 0 ? (
                    <ButtonLink onClick={() => handleImg(ic)}>View</ButtonLink>
             ) : (
                <span style={{ color: 'gray' }}>View</span>
             );
            },
        },
        {
          title: "License Status",
          dataIndex: "licenseStatus",
          ...getColumnSearchProps("License Status", "licenseStatus", searchTextState, searchedColumnState),
          render: (data) => <Tag color={DRIVER_LICENSE_STATUS_COLOR_BY_CODE[data]}>{DRIVER_LICENSE_STATUS_BY_CODE[data]}</Tag>,
        },
        {
          title: "Company",
          dataIndex: "company",
          ...getColumnSearchProps("Company", 'company', searchTextState, searchedColumnState),
        },
        {
            title: "Last Updated By",
            // dataIndex: "updatedAt",
            defaultSortOrder: "descend",
            width: 200,
            sorter: (a, b) => a.updatedAt - b.updatedAt,
            render: data => {

                const name = data.updatedBy === uid ? uname : data.updatedBy

                return (
                    <>
                        <>{name}</><br/>
                        <>{parseDate(Number(data.updatedAt))}</>
                    </>
                )
            }
                
          },
        {
            title: "Created By",
            // dataIndex: "createdAt",
            width: 200,
            sorter: (a, b) => a.createdAt - b.createdAt,
            render: data => {

                const name = data.createdBy === uid ? uname : data.createdBy

                return (
                    <>
                        <>{name}</><br/>
                        <>{parseDate(Number(data.createdAt))}</>
                    </>
                )
            }
          },
        {
            title: "Actions",
            width: 170,
            fixed: 'right',
            render: data => {

                const items = [
                    {
                        key: '1',
                        label: 
                            <span 
                                className="ButtonLink"
                                aria-hidden="true"
                                onClick = {() => navigate(`/update/driver`, {state: { data: { ...data, title: 'Edit' } }})}
                            >
                                Edit
                            </span>
                    },
                    {
                        key: '2',
                        label: 
                        <ModalStandard 
                            onClick={() => dispatch(REDUX_ACTIONS.v3_drivers.update_driver_request({
                                uid,
                                drvid: data.drvid,
                                driver: {
                                    ...data,
                                    status: 1
                                }
                            }))}
                            disabled={data.status === 1}
                            title={'Mark as active'}
                            content={'You are about to mark this driver as active.'}
                            subcontent={`${data.name || '-'}`}
                        >
                            Mark as active
                        </ModalStandard>
                    },
                    {
                        key: '3',
                        label: 
                        <ModalStandard 
                            onClick={() => dispatch(REDUX_ACTIONS.v3_drivers.update_driver_request({
                                uid,
                                drvid: data.drvid,
                                driver: {
                                    ...data,
                                    status: 0
                                }
                            }))}
                            disabled={data.status === 0}
                            title={'Mark as inactive'}
                            content={'You are about to mark this driver as inactive.'}
                            subcontent={`${data.name || '-'}`}
                            // icon={<></>}
                        >
                            Mark as inactive
                        </ModalStandard>
                    }
                ]

                return (
                    <Dropdown menu={{ items }}>
                        <EditOutlined />
                    </Dropdown>
                )
            }
          }
      ];

    useEffect(() => {
    //   setIsLoaded(Object.values(drivers).length > 0);
    if (
        (prevVehicles !== drivers)
    ) {
        refreshDataSrc()
          setIsLoaded(true);
        }
    }, [drivers])

    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
        const file = event.target.files[0];

        if (file) {
            uploadHDLR.decodeFile(file, (d) => {
                const rData = xlsx_handler.readXLSXFile(d); // read data

                const rDataF = rData.map(d => {

                    return {
                        name: d["Driver Name*"],
                        refid: d["Reference ID"],
                        contact: d["Driver Contact*"]?.toString(),
                        icNumber: d["IC Number*"]?.toString(),
                        icSerialNumber: d["IC Serial Number"]?.toString(),
                        licenseExpiredAt: TIME.parseExcelDateToEpoch(d["License Expiry Date"]) || 0,
                        company: uid
                    }
                })

                const isNotDuplicated = rDataF.length === new Set(rDataF.map(x => x.name)).size

                if(isNotDuplicated) {
                    dispatch(REDUX_ACTIONS.v3_drivers.create_drivers_request({
                        drivers: rDataF,
                        uid: uid
                    }))
                    event.target.value = null
                } else {
                    message.warning('Duplicated Driver Name detected. Please change Driver Name into unique value!')
                }

            });
        }
    };

    const BulkCreateDriverItems = [
        {
            key: '1',
            label: 
            <>
                <span 
                    className="ButtonLink"
                    aria-hidden="true"
                    onClick = {() => fileInputRef.current.click()}
                >
                    Upload File
                </span>
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange} 
                    style={{ display: "none" }} 
                />
            </>
        },
        {
            key: '2',
            label: 
                <span 
                    className="ButtonLink"
                    aria-hidden="true"
                    onClick = {() => templateHDLR.getDriverTemplateXLSXFromS3()}
                >
                    Download Template
                </span>
        },
    ]

    return (
        <div >
            {/* <PrimaryButton htmlType={'submit'} style={{ float: 'right'}} onClick={() => navigate (`/create/driver`)}>+ Add New Driver</PrimaryButton> */}
            <div style={{ display: 'flex', gap: '7px', float: 'right', marginBottom: '15px'}}>
                <Dropdown menu={{ items: BulkCreateDriverItems }}>
                    <Button >+ Bulk Create Drivers</Button>
                </Dropdown>
                <PrimaryButton htmlType={'submit'} onClick={() => navigate (`/create/driver`)}>+ Add New Driver</PrimaryButton>
            </div>
            {  !isLoaded ? 
                <Loading /> :
                dataSource.length > 0 ?
                <>
                    <Form form={form} component={false} >
                        <Table
                            loading={style.loading.general}
                            columns={columns && columns.filter(c => !c.isHidden)}
                            dataSource={dataSource}
                            pagination={true}
                            scroll={{
                                y: window.innerHeight,
                                x: columns && columns.length * 180
                            }}
                            components={{ body: { cell: EditableCell }}}
                        /> 
                    </Form>
                </>
                :
                    <EmptyIndicator />
            }
            <ModalImage isOpen={isOpen} setIsOpen={setIsOpen} extra={rec} />
        </div>
    )
}

export default DriverPanel